
import { useJournalistProfileData } from './useJournalistProfileData';
import { useJournalistArticles } from './useJournalistArticles';
import { useJournalistFollow } from './useJournalistFollow';
import { useProfileUpdate } from './useProfileUpdate';
import { JournalistProfileHookResult } from './types';

// This hook combines multiple journalist-related hooks into a single interface
export const useJournalistProfile = (): JournalistProfileHookResult => {
  const {
    journalist,
    setJournalist,
    isLoading: profileLoading,
    error: profileError,
    isFollowing,
    setIsFollowing,
    isOwnProfile
  } = useJournalistProfileData();
  
  const { articles, isLoading: articlesLoading, error: articlesError } = useJournalistArticles(journalist?.id);
  
  const { toggleFollow, isLoading: followLoading } = useJournalistFollow();

  // Create a wrapper function to handle the toggleFollow action for the specific journalist
  const handleToggleFollow = async () => {
    if (!journalist) return;
    
    try {
      const isNowFollowing = await toggleFollow(journalist.id);
      setIsFollowing(isNowFollowing);
      
      // Update the journalist's follower count if needed
      if (setJournalist && journalist) {
        setJournalist({
          ...journalist,
          metrics: {
            ...journalist.metrics,
            followers: journalist.metrics.followers + (isNowFollowing ? 1 : -1)
          }
        });
      }
    } catch (error) {
      console.error('Error toggling follow status:', error);
    }
  };
  
  const { updateProfile } = useProfileUpdate(journalist, setJournalist, isOwnProfile);
  
  // Combine loading states
  const isLoading = profileLoading || articlesLoading || followLoading;
  // Combine errors (prioritize profile error over articles error)
  const error = profileError || articlesError;
  
  return {
    journalist,
    setJournalist,
    articles,
    isLoading,
    error,
    isFollowing,
    setIsFollowing,
    isOwnProfile,
    toggleFollow: handleToggleFollow,
    updateProfile
  };
};
